import { useEffect, useRef } from "react";
import "./MapComponent.css";

interface MapComponentProps {
  lat: number;
  lon: number;
  streetAddress?: string | null;
}

declare global {
  interface Window {
    L: any;
  }
}

const MapComponent = ({ lat, lon, streetAddress }: MapComponentProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const tileLayerRef = useRef<any>(null);

  useEffect(() => {
    if (!mapRef.current || !window.L) return;

    // Destroy previous map if it exists
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
      tileLayerRef.current = null;
    }

    // Create new map
    const map = window.L.map(mapRef.current).setView([lat, lon], 15);
    mapInstanceRef.current = map;

    // OSM tiles in both themes; dark mode inverts them with a CSS filter
    // keyed off <html data-theme="dark"> (MapComponent.css), so the tile layer
    // carries a class instead of reading the colour mode here.
    // (CARTO dark_all now demands an API key and renders "API KEY REQUIRED".)
    const tileLayer = window.L.tileLayer(
      'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
        className: 'ips-map-tiles',
      }
    ).addTo(map);

    tileLayerRef.current = tileLayer;

    // Add marker
    const popupContent = streetAddress 
      ? `📍 ${streetAddress}`
      : '📍';
    const marker = window.L.marker([lat, lon])
      .addTo(map)
      .bindPopup(popupContent)
      .openPopup();
    
    markerRef.current = marker;

    // Cleanup on unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
        tileLayerRef.current = null;
      }
    };
  }, [lat, lon, streetAddress]);

  return (
    <div
      ref={mapRef}
      style={{
        height: '300px',
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        zIndex: 1,
        position: 'relative',
      }}
    />
  );
};

export default MapComponent;

